import { useNavigate } from "@tanstack/react-router";
import {
  BriefcaseBusiness,
  Building2,
  CheckCircle2,
  ClipboardList,
  FolderKanban,
  LayoutDashboard,
  Loader2,
  Search,
  UserRound,
  X,
  type LucideIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
  searchGlobal,
  type GlobalSearchResult,
  type GlobalSearchResultType,
} from "@shared/api/globalSearch";
import { buildNavigationSearchResults } from "./navigationCatalog";
import type { SidebarItem } from "@shared/components/Sidebar/sidebar.config";
import {
  notifyGlobalNavigation,
  queueGlobalNavigation,
} from "@shared/navigation/globalNavigation";

type Props = {
  collapsed: boolean;
  visibleItems: SidebarItem[];
};

const TYPE_CONFIG: Record<
  GlobalSearchResultType,
  { label: string; icon: LucideIcon }
> = {
  apartado: { label: "Apartado", icon: LayoutDashboard },
  subapartado: { label: "Subapartado", icon: FolderKanban },
  seccion: { label: "Sección", icon: ClipboardList },
  subseccion: { label: "Subsección", icon: CheckCircle2 },
  usuario: { label: "Usuario", icon: UserRound },
  cliente: { label: "Cliente", icon: Building2 },
  proyecto: { label: "Proyecto", icon: FolderKanban },
  valoracion: { label: "Valoración", icon: BriefcaseBusiness },
  contacto: { label: "Contacto", icon: UserRound },
  campana: { label: "Campaña", icon: BriefcaseBusiness },
  empresa: { label: "Empresa", icon: Building2 },
  departamento: { label: "Departamento", icon: Building2 },
};

function mergeResults(
  navigationResults: GlobalSearchResult[],
  recordResults: GlobalSearchResult[],
): GlobalSearchResult[] {
  const unique = new Map<string, GlobalSearchResult>();

  for (const result of [...navigationResults, ...recordResults]) {
    const navigationKey = result.navigation
      ? `${result.navigation.scope}:${result.navigation.section ?? ""}:${result.navigation.subsection ?? ""}:${result.navigation.anchor ?? ""}`
      : "";
    const key = [
      result.type,
      result.url,
      result.title,
      result.meta ?? "",
      navigationKey,
    ].join("|");

    if (!unique.has(key)) unique.set(key, result);
  }

  return Array.from(unique.values());
}

export function GlobalSearch({ collapsed, visibleItems }: Props) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [recordResults, setRecordResults] = useState<GlobalSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const navigationResults = useMemo(() => {
    const normalized = query.trim();
    return normalized.length >= 2
      ? buildNavigationSearchResults(normalized, visibleItems)
      : [];
  }, [query, visibleItems]);

  const results = useMemo(
    () => mergeResults(navigationResults, recordResults),
    [navigationResults, recordResults],
  );

  const groupedResults = useMemo(() => {
    const groups = new Map<string, GlobalSearchResult[]>();

    for (const result of results) {
      const current = groups.get(result.section) ?? [];
      current.push(result);
      groups.set(result.section, current);
    }

    return Array.from(groups.entries());
  }, [results]);

  const closeSearch = useCallback(() => {
    setOpen(false);
    setQuery("");
    setRecordResults([]);
    setError(null);
    setLoading(false);
    setActiveIndex(0);
  }, []);

  const openResult = useCallback(
    (result: GlobalSearchResult) => {
      const target = result.navigation;
      if (target) queueGlobalNavigation(target);

      closeSearch();

      const navigation = navigate({ to: result.url });
      if (target) {
        void Promise.resolve(navigation).finally(() => {
          notifyGlobalNavigation(target);
        });
      }
    },
    [closeSearch, navigate],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        if (open) {
          closeSearch();
        } else {
          setOpen(true);
        }
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeSearch, open]);

  useEffect(() => {
    if (!open) return;

    const frame = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const normalized = query.trim();
    if (normalized.length < 2) {
      setRecordResults([]);
      setLoading(false);
      setError(null);
      setActiveIndex(0);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setRecordResults([]);
    setActiveIndex(0);

    const timeout = window.setTimeout(() => {
      void searchGlobal(normalized, controller.signal)
        .then((items) => {
          setRecordResults(items);
          setActiveIndex(0);
        })
        .catch((requestError: unknown) => {
          if (controller.signal.aborted) return;
          console.error("Error en el buscador global", requestError);
          setRecordResults([]);
          setError(
            "No se pudieron cargar los registros. La navegación por apartados sigue disponible.",
          );
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 250);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [open, query]);

  useEffect(() => {
    if (activeIndex < results.length) return;
    setActiveIndex(results.length > 0 ? results.length - 1 : 0);
  }, [activeIndex, results.length]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeSearch();
        return;
      }

      if (results.length === 0) return;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((current) => (current + 1) % results.length);
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((current) =>
          current <= 0 ? results.length - 1 : current - 1,
        );
      }

      if (event.key === "Enter") {
        event.preventDefault();
        const result = results[activeIndex];
        if (result) openResult(result);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeIndex, closeSearch, open, openResult, results]);

  const modal =
    open && typeof document !== "undefined" ? (
      <div
        className="fixed inset-0 z-[220] flex items-start justify-center bg-slate-950/60 px-3 pb-6 pt-[8vh] backdrop-blur-sm sm:px-6"
        role="dialog"
        aria-modal="true"
        aria-label="Buscador global de INPRONEXUS"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeSearch();
        }}
      >
        <div className="flex max-h-[82vh] w-full max-w-3xl flex-col overflow-hidden rounded-[30px] border border-white/70 bg-white shadow-[0_36px_110px_rgba(15,23,42,.34)]">
          <div className="flex items-center gap-3 border-b border-slate-200 bg-[linear-gradient(135deg,#f8fbf9,#eef7f3)] px-4 py-4 sm:px-5">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[#0b4a2f] text-white shadow-[0_12px_28px_rgba(11,74,47,.2)]">
              <Search size={19} />
            </div>

            <div className="min-w-0 flex-1">
              <label htmlFor="inpronexus-global-search" className="sr-only">
                Buscar en INPRONEXUS
              </label>
              <input
                ref={inputRef}
                id="inpronexus-global-search"
                type="search"
                autoComplete="off"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar apartados, secciones, usuarios, clientes, contactos..."
                className="w-full border-0 bg-transparent p-0 text-base font-bold text-slate-950 outline-none placeholder:font-semibold placeholder:text-slate-400 focus:ring-0 sm:text-lg"
              />
              <div className="mt-1 text-xs font-semibold text-slate-500">
                Busca navegación y registros desde cualquier apartado. Solo verás zonas e información a las que tienes acceso.
              </div>
            </div>

            <button
              type="button"
              onClick={closeSearch}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800"
              title="Cerrar buscador"
              aria-label="Cerrar buscador"
            >
              <X size={17} />
            </button>
          </div>

          <div className="min-h-[190px] flex-1 overflow-y-auto bg-white p-3 sm:p-4">
            {query.trim().length < 2 && (
              <div className="flex min-h-[165px] flex-col items-center justify-center text-center">
                <div className="grid h-12 w-12 place-items-center rounded-2xl bg-emerald-50 text-emerald-700">
                  <Search size={20} />
                </div>
                <div className="mt-3 text-sm font-black text-slate-800">
                  Escribe al menos 2 caracteres
                </div>
                <div className="mt-1 max-w-md text-xs font-semibold leading-5 text-slate-500">
                  Puedes buscar apartados, subapartados, secciones internas y registros de la aplicación.
                </div>
              </div>
            )}

            {query.trim().length >= 2 && loading && results.length === 0 && (
              <div className="flex min-h-[165px] items-center justify-center gap-2 text-sm font-black text-emerald-700">
                <Loader2 size={18} className="animate-spin" />
                Buscando en INPRONEXUS...
              </div>
            )}

            {error && (
              <div className="mb-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-bold text-amber-800">
                {error}
              </div>
            )}

            {!loading &&
              !error &&
              query.trim().length >= 2 &&
              results.length === 0 && (
                <div className="flex min-h-[165px] flex-col items-center justify-center text-center">
                  <div className="text-sm font-black text-slate-800">
                    Sin resultados
                  </div>
                  <div className="mt-1 text-xs font-semibold text-slate-500">
                    No se encontraron apartados, secciones ni registros visibles que coincidan con “{query.trim()}”.
                  </div>
                </div>
              )}

            {groupedResults.length > 0 && (
              <div className="space-y-5">
                {groupedResults.map(([section, items]) => (
                  <section key={section}>
                    <div className="mb-2 flex items-center gap-2 px-1">
                      <span className="text-[11px] font-black uppercase tracking-[0.16em] text-emerald-700">
                        {section}
                      </span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-black text-slate-500">
                        {items.length}
                      </span>
                    </div>

                    <div className="space-y-1.5">
                      {items.map((result) => {
                        const resultIndex = results.indexOf(result);
                        const active = resultIndex === activeIndex;
                        const config = TYPE_CONFIG[result.type];
                        const Icon = config.icon;

                        return (
                          <button
                            key={`${result.type}-${result.url}-${result.title}-${resultIndex}`}
                            type="button"
                            onClick={() => openResult(result)}
                            onMouseEnter={() => setActiveIndex(resultIndex)}
                            className={[
                              "group flex w-full items-start gap-3 rounded-2xl border px-3.5 py-3 text-left transition sm:px-4",
                              active
                                ? "border-emerald-200 bg-emerald-50/80 shadow-[0_8px_24px_rgba(16,185,129,.08)]"
                                : "border-transparent bg-white hover:border-slate-200 hover:bg-slate-50",
                            ].join(" ")}
                          >
                            <span
                              className={[
                                "mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl border transition",
                                active
                                  ? "border-emerald-200 bg-white text-emerald-700"
                                  : "border-slate-200 bg-slate-50 text-slate-500 group-hover:text-emerald-700",
                              ].join(" ")}
                            >
                              <Icon size={16} />
                            </span>

                            <span className="min-w-0 flex-1">
                              <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span className="truncate text-sm font-black text-slate-950">
                                  {result.title}
                                </span>
                                <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-slate-500">
                                  {result.badge ?? config.label}
                                </span>
                              </span>

                              {result.subtitle && (
                                <span className="mt-1 block line-clamp-2 text-xs font-semibold leading-5 text-slate-600">
                                  {result.subtitle}
                                </span>
                              )}

                              {result.meta && (
                                <span className="mt-1 block truncate text-[11px] font-bold text-slate-400">
                                  {result.meta}
                                </span>
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50/80 px-4 py-3 text-[11px] font-bold text-slate-500 sm:px-5">
            <span>
              {results.length > 0
                ? `${results.length} resultados visibles`
                : "Búsqueda global"}
            </span>
            <span className="hidden items-center gap-3 sm:flex">
              <span>↑ ↓ navegar</span>
              <span>Enter abrir</span>
              <span>Esc cerrar</span>
            </span>
          </div>
        </div>
      </div>
    ) : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={collapsed ? "Buscar en INPRONEXUS (Ctrl + K)" : undefined}
        aria-label="Buscar en INPRONEXUS"
        className={[
          "mb-5 flex w-full items-center rounded-[22px] border border-white/12 bg-white/[0.07] text-white/80 shadow-[0_12px_30px_rgba(0,0,0,.10)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#00d09c]/30 hover:bg-white/[0.10] hover:text-white",
          collapsed ? "justify-center px-0 py-3" : "gap-3 px-3.5 py-3",
        ].join(" ")}
      >
        <Search size={18} className="shrink-0" />
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1 truncate text-left text-sm font-black">
              Buscar en INPRONEXUS
            </span>
            <span className="shrink-0 rounded-lg border border-white/10 bg-black/10 px-2 py-1 text-[10px] font-black text-white/55">
              Ctrl K
            </span>
          </>
        )}
      </button>

      {modal ? createPortal(modal, document.body) : null}
    </>
  );
}
